// ./src/components/features/EventTickets/TicketRefundDrawer/TicketRefundDrawer.tsx
//
// Refund an issued ticket (or the whole order). Modes:
//   • amount blank = full refund of the ticket price; a value = partial refund.
//   • keep ticket = refund money but leave the ticket valid (no void).
//   • bypass processor = record the refund without hitting the payment processor
//     (cash-back / offline correction).
// The server writes a refund row to the payment ledger and rolls up the original.
//
// Uses AppDrawer (the project's responsive drawer standard). The Refund button shows a
// loading spinner while the refund is in flight (a Stripe refund can take a few seconds),
// and any error is surfaced in the drawer's red alert right above the footer buttons.
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Stack, Text, NumberInput, Switch, Textarea, Box, Group, SegmentedControl } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import { EventTicket } from "@/utils/api";
import { AppDrawer } from "@/components/generic";
import { getFormErrors } from "@/utils";
import { useFormSnapshot } from "@/hooks";

// Both refund paths (a single ticket and the whole order) name their amount refusals
// `amount`, so they land on the amount input instead of the footer.
const KNOWN_FORM_FIELDS = ["amount"] as const;

/** What the drawer emits — an order-aware refund (single ticket or the whole order). */
export interface TicketRefundEmit {
  scope: "ticket" | "order";
  amount: number | null;
  keepTickets: boolean;
  bypassProcessor: boolean;
  note: string | null;
}

export interface TicketRefundDrawerProps {
  opened: boolean;
  onClose: () => void;
  ticket: EventTicket | null;
  /** Runs the refund; resolves with the OpResult so the drawer can surface errors. */
  onConfirm: (params: TicketRefundEmit) => Promise<OpResult>;
  inProgress?: boolean;
}

const fmtMoney = (amount?: string | null, currency?: string | null): string => {
  if (amount == null || amount === "") {
    return "—";
  }

  const n = Number(amount);
  if (!Number.isFinite(n)) {
    return "—";
  }

  const v = n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return currency ? `${currency} ${v}` : v;
};

const TicketRefundDrawerComponent: React.FC<TicketRefundDrawerProps> = ({
  opened,
  onClose,
  ticket,
  onConfirm,
  inProgress = false,
}) => {
  const { t } = useTranslation(["ticketSales", "common"]);

  const [scope, setScope] = useState<"ticket" | "order">("ticket");
  const [amount, setAmount] = useState<number | "">("");
  const [keepTickets, setKeepTickets] = useState(false);
  const [bypassProcessor, setBypassProcessor] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState<OpResult | null>(null);

  const hasOrder = !!ticket?.ticketOrderId;
  const priceNum = ticket?.price != null && Number.isFinite(Number(ticket.price)) ? Number(ticket.price) : null;
  // Cap the amount only for a single-ticket refund; the whole-order max is enforced server-side.
  const amountMax = scope === "ticket" ? priceNum ?? undefined : undefined;

  // Esc / a click outside / Cancel ask before discarding a typed amount, a note or a changed option (#123).
  const { isDirty, seedFrom, clear } = useFormSnapshot({ scope, amount, keepTickets, bypassProcessor, note });

  useEffect(() => {
    if (!opened) {
      clear();

      return;
    }

    setScope("ticket");
    setAmount("");
    setKeepTickets(false);
    setBypassProcessor(false);
    setNote("");
    seedFrom({ scope: "ticket", amount: "", keepTickets: false, bypassProcessor: false, note: "" });
    setError(null);
  }, [opened, clear, seedFrom]);

  const handleConfirm = useCallback(async () => {
    setError(null);

    const result = await onConfirm({
      scope,
      amount: amount === "" ? null : Number(amount),
      keepTickets,
      bypassProcessor,
      note: note.trim() || null,
    });

    if (result.code === OP_RESULT_CODES.OK) {
      onClose();

      return;
    }

    setError(result);
  }, [scope, amount, keepTickets, bypassProcessor, note, onConfirm, onClose]);

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(error, KNOWN_FORM_FIELDS),
    [error]
  );
  const isPartial = scope === "ticket" && amount !== "" && priceNum != null && Number(amount) < priceNum;
  const overPrice = scope === "ticket" && amount !== "" && priceNum != null && Number(amount) > priceNum;

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={t("ticketSales:refund.title", { defaultValue: "Refund ticket" })}
      size="md"
      mobileFitContent
      closable={!inProgress}
      actionType="confirm-cancel"
      onConfirm={handleConfirm}
      confirmLabel={t("ticketSales:refund.confirm", { defaultValue: "Refund" })}
      confirmIcon="Coin"
      confirmColor="red"
      confirmDisabled={overPrice}
      loading={inProgress}
      errors={genericErrors.length ? genericErrors : null}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      cancelLabel={t("common:buttons.cancel", { defaultValue: "Cancel" })}
      isDirty={isDirty}
    >
      <Stack gap="md">
        <Group justify="space-between" wrap="nowrap">
          <Text size="md" c="dimmed">
            {ticket?.ticketCode
              ? t("ticketSales:refund.ticket", { defaultValue: "Ticket {{code}}", code: ticket.ticketCode })
              : t("ticketSales:refund.ticketGeneric", { defaultValue: "This ticket" })}
          </Text>
          <Text size="md" fw={600}>
            {fmtMoney(ticket?.price, ticket?.currencyCode)}
          </Text>
        </Group>

        {hasOrder && (
          <SegmentedControl
            fullWidth
            value={scope}
            onChange={(v) => setScope(v as "ticket" | "order")}
            data={[
              { label: t("ticketSales:refund.scopeTicket", { defaultValue: "This ticket" }), value: "ticket" },
              { label: t("ticketSales:refund.scopeOrder", { defaultValue: "Whole order" }), value: "order" },
            ]}
          />
        )}

        <NumberInput
          label={t("ticketSales:refund.amount", { defaultValue: "Refund amount" })}
          description={
            scope === "order"
              ? t("ticketSales:refund.amountHintOrder", { defaultValue: "Leave blank to refund the full remaining order total." })
              : t("ticketSales:refund.amountHint", { defaultValue: "Leave blank to refund the full ticket price." })
          }
          value={amount}
          onChange={(v) => setAmount(v === "" ? "" : Number(v))}
          min={0}
          max={amountMax}
          decimalScale={2}
          fixedDecimalScale={false}
          prefix={ticket?.currencyCode ? `${ticket.currencyCode} ` : undefined}
          size="md"
          // The local check keeps the user from submitting an impossible amount; the server's
          // own refusal (it owns the real limit) takes the same slot once one comes back.
          error={
            overPrice
              ? t("ticketSales:refund.overPrice", { defaultValue: "Can't exceed the ticket price." })
              : fieldErrors.amount
          }
        />

        {isPartial && (
          <Text size="sm" c="dimmed">
            {t("ticketSales:refund.partialNote", { defaultValue: "Partial refund — the original payment is marked partially refunded." })}
          </Text>
        )}

        <Box>
          <Switch
            label={
              scope === "order"
                ? t("ticketSales:refund.keepTickets", { defaultValue: "Keep the tickets valid" })
                : t("ticketSales:refund.keepTicket", { defaultValue: "Keep the ticket valid" })
            }
            description={t("ticketSales:refund.keepTicketHint", { defaultValue: "Refund the money but don't void the ticket(s) — e.g. make them complimentary." })}
            checked={keepTickets}
            onChange={(e) => setKeepTickets(e.currentTarget.checked)}
            size="md"
          />
        </Box>

        {!keepTickets && (
          <Text size="sm" c="dimmed">
            {t("ticketSales:refund.guestNote", {
              defaultValue: "Voiding a ticket marks its attendee as not attending, so the head count stays accurate.",
            })}
          </Text>
        )}

        <Box>
          <Switch
            label={t("ticketSales:refund.bypass", { defaultValue: "Bypass payment processor" })}
            description={t("ticketSales:refund.bypassHint", { defaultValue: "Record the refund only (cash-back / offline) — don't charge it back through the processor." })}
            checked={bypassProcessor}
            onChange={(e) => setBypassProcessor(e.currentTarget.checked)}
            size="md"
          />
        </Box>

        <Textarea
          label={t("ticketSales:refund.note", { defaultValue: "Note (optional)" })}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          minRows={2}
          autosize
          size="md"
        />
      </Stack>
    </AppDrawer>
  );
};

export const TicketRefundDrawer = React.memo(TicketRefundDrawerComponent);
